import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import React, { useContext, useMemo } from 'react';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { CurrencyInput, ValidationErrorMessage } from '@components/common/form';
import { INPUT_TYPES } from '@components/common/form/constants/fieldTypes';
import Tooltip from '@components/common/tooltip/Tooltip';
import { usePermissions } from '@hooks/usePermissions';
import useValidateAPTCAmount from '@hooks/useValidateAPTCAmount';
import { useSelector } from 'react-redux';
import { currencyChange } from '../../common/enrollmentUtils';
import { IEnrollmentPremiumDto } from '../../common/types';
import { EnrollmentContext } from '../../context/MemberMyEnrollmentProvider';
import { StyledField } from '../../styles/EnrollmentSharedStyled';

const ColumnMaxStateSubsidy = ({ premium }: { premium: IEnrollmentPremiumDto }) => {
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { viewEditableFields } = usePermissions();
    const validateSubsidy = useValidateAPTCAmount();
    const { enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const {
        watch,
        setValue,
        clearErrors,
        formState: { errors },
    } = enrollmentPremiumFormContext;
    const values = watch();

    const { isEnrollmentPendingOrPendCancelled } = usePermissions();

    const onMaxStateSubsidyChange = e => {
        validateSubsidy({ ...premium, maxStateSubsidy: Number(parseFloat(e.target.value).toFixed(2)) });
        currencyChange(setValue, 'maxStateSubsidy', premium, e.target.value);
    };

    const isDisabled = useMemo(
        () =>
            !premium.isFinancial ||
            (!selectedEnrollment.isStateSubsidyEnabled && premium.stateSubsidyAmount <= 0) ||
            !userPermissions?.memberEnrlmntAptcEditable ||
            isEnrollmentPendingOrPendCancelled(selectedEnrollment),
        [premium.isFinancial],
    );

    return (
        <ConditionalRendering
            isTrue={selectedEnrollment.isStateSubsidyEnabled && viewEditableFields(userPermissions, values, premium)}
        >
            <StyledField>
                <CurrencyInput
                    // @ts-ignore
                    label={{
                        wrapperClasses: [''],
                        labelClasses: [''],
                        labelText: '',
                        showRequired: false,
                        isInputRequired: false,
                    }}
                    input={{
                        id: 'maxStateSubsidy',
                        isInputRequired: true,
                        type: INPUT_TYPES.TEXT,
                        value: premium.maxStateSubsidy,
                        disabled: isDisabled,
                        dataTestId: 'maxStateSubsidy',
                        name: 'maxStateSubsidy',
                        placeholder: '',
                    }}
                    actions={{
                        handleChange: e => onMaxStateSubsidyChange(e),
                    }}
                    maskOptions={{
                        allowDecimal: true,
                        prefix: '',
                        decimalLimit: '2',
                        integerLimit: 10,
                        includeThousandsSeparator: false,
                        allowLeadingZeroes: true,
                    }}
                    showIcon={true}
                />
                <ConditionalRendering isTrue={!premium.isFinancial}>
                    <Tooltip
                        index={`groupMaxStateSubsidyTooltip-${premium.month}`}
                        id="questinMark"
                        elementId="groupMaxStateSubsidyTooltip"
                        content="member.enrollment_list.premiumhistory.tooltip.maxStateSubsidy"
                        place="top"
                        icon={faQuestionCircle}
                        dataAutomationId="groupMaxStateSubsidyTooltip"
                    />
                </ConditionalRendering>
            </StyledField>

            <ConditionalRendering isTrue={premium.isFinancial}>
                <ValidationErrorMessage
                    isVisible={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.maxStateSubsidy}
                    messageText={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.maxStateSubsidy?.message}
                />
            </ConditionalRendering>
        </ConditionalRendering>
    );
};

export default ColumnMaxStateSubsidy;
